import classNames from "classnames";
import { useState } from "react";
import NoData from "../../assets/no-data.svg?react";
import BottomSheet from "../../components/BottomSheet";
import Icon from "../../components/Icon";
import Tooltip from "../../components/icons/tooltip.svg?react";
import IllustratedMessage from "../../components/IllustratedMessage";
import { Bike, Preferences, Settings, Value } from "../../http/onlyclicksApi";
import { translate } from "../../i18n";
import { Language } from "../../types";
import styles from "./HistoryTable.module.css";

interface Props {
  settings: Settings[] | undefined;
  bike: Bike | undefined;
  preferences: Preferences | undefined;
}

const formatDay = (date: string): string =>
  Intl.DateTimeFormat(undefined, {
    day: "2-digit",
  }).format(new Date(date));

const formatMonth = (date: string): string =>
  Intl.DateTimeFormat(undefined, {
    month: "short",
  }).format(new Date(date));

const formatTime = (date: string): string =>
  Intl.DateTimeFormat(undefined, {
    hour: "2-digit",
    minute: "2-digit",
  }).format(new Date(date));

const createClassNames = (index: number) =>
  classNames({
    [styles.currColumn]: index === 0,
    [styles.prevColumn]: index > 0,
  });

const createClassNamesWithValue = (
  index: number,
  value: Value<number> | undefined,
) =>
  classNames(styles.settingValue, createClassNames(index), {
    [styles.diffPositive]:
      value?.curr !== undefined &&
      value.prev !== undefined &&
      value.curr > value.prev,
    [styles.diffNegative]:
      value?.curr !== undefined &&
      value.prev !== undefined &&
      value.curr < value.prev,
  });

const formatPercent = (v: number): string =>
  Intl.NumberFormat(navigator.language, {
    style: "percent",
    maximumFractionDigits: 0,
  }).format(v / 100);

const HistoryTable = ({ settings, bike, preferences }: Props) => {
  const colSpan = settings?.length ?? 0;
  const i18n = translate(Language.English);
  const [openNotesDialogIndex, setOpenNotesDialogIndex] = useState<number>();

  return (
    <>
      {(settings?.length ?? 0) > 0 ? (
        <div className={styles.wrapper}>
          <table className={styles.table}>
            <thead>
              <tr>
                <th className={styles.settingLabel}></th>
                {settings?.map((s, i) => {
                  return (
                    <th
                      key={`date-${i}`}
                      className={classNames(styles.date, createClassNames(i))}
                    >
                      <time dateTime={s.created_at}>
                        <strong>{formatDay(s.created_at)}</strong>
                        <small>{formatMonth(s.created_at)}</small>
                        <small>{formatTime(s.created_at)}</small>
                      </time>
                    </th>
                  );
                })}
              </tr>
            </thead>
            {bike?.fork && (
              <tbody>
                <tr className={styles.group}>
                  <th className={styles.groupLabel}>{i18n.fork}</th>
                  <th colSpan={colSpan}></th>
                </tr>
                {bike.fork.show_sag && (
                  <tr>
                    <th className={styles.settingLabel}>
                      <small>{i18n.sag}</small>
                    </th>
                    {settings?.map((s, i) => {
                      return (
                        <td
                          key={`fork-sag-${i}`}
                          className={createClassNamesWithValue(i, s.fork?.sag)}
                        >
                          {s.fork?.sag?.curr !== undefined &&
                            formatPercent(s.fork?.sag?.curr)}
                        </td>
                      );
                    })}
                  </tr>
                )}
                {bike.fork.type === "air" && (
                  <tr>
                    <th className={styles.settingLabel}>
                      <small>{i18n.air_pressure}</small>
                    </th>
                    {settings?.map((s, i) => {
                      return (
                        <td
                          key={`fork-air-pressure-${i}`}
                          className={createClassNamesWithValue(i, s.fork?.psi)}
                        >
                          {s.fork?.psi?.curr}
                        </td>
                      );
                    })}
                  </tr>
                )}
                {bike.fork.type === "coil" && (
                  <tr>
                    <th className={styles.settingLabel}>
                      <small>{i18n.spring_weight}</small>
                    </th>
                    {settings?.map((s, i) => {
                      return (
                        <td
                          key={`fork-spring-weight-${i}`}
                          className={createClassNamesWithValue(i, s.fork?.lbs)}
                        >
                          {s.fork?.lbs?.curr}
                        </td>
                      );
                    })}
                  </tr>
                )}
                {bike.fork.show_lsc && (
                  <tr>
                    <th className={styles.settingLabel}>
                      <small>{i18n.lsc(true)}</small>
                    </th>
                    {settings?.map((s, i) => {
                      return (
                        <td
                          key={`fork-lsc-${i}`}
                          className={createClassNamesWithValue(i, s.fork?.lsc)}
                        >
                          {s.fork?.lsc?.curr}
                        </td>
                      );
                    })}
                  </tr>
                )}
                {bike.fork.show_hsc && (
                  <tr>
                    <th className={styles.settingLabel}>
                      <small>{i18n.hsc(true)}</small>
                    </th>
                    {settings?.map((s, i) => {
                      return (
                        <td
                          key={`fork-hsc-${i}`}
                          className={createClassNamesWithValue(i, s.fork?.hsc)}
                        >
                          {s.fork?.hsc?.curr}
                        </td>
                      );
                    })}
                  </tr>
                )}
                {bike.fork.show_lsr && (
                  <tr>
                    <th className={styles.settingLabel}>
                      <small>{i18n.lsr(true, true)}</small>
                    </th>
                    {settings?.map((s, i) => {
                      return (
                        <td
                          key={`fork-lsr-${i}`}
                          className={createClassNamesWithValue(i, s.fork?.lsr)}
                        >
                          {s.fork?.lsr?.curr}
                        </td>
                      );
                    })}
                  </tr>
                )}
                {bike.fork.show_hsr && (
                  <tr>
                    <th className={styles.settingLabel}>
                      <small>{i18n.hsr(true)}</small>
                    </th>
                    {settings?.map((s, i) => {
                      return (
                        <td
                          key={`fork-hsr-${i}`}
                          className={createClassNamesWithValue(i, s.fork?.hsr)}
                        >
                          {s.fork?.hsr?.curr}
                        </td>
                      );
                    })}
                  </tr>
                )}
                {bike.fork.show_volume_spacers && (
                  <tr>
                    <th className={styles.settingLabel}>
                      <small>{i18n.volume_spacers}</small>
                    </th>
                    {settings?.map((s, i) => {
                      return (
                        <td
                          key={`fork-volume-spacers-${i}`}
                          className={createClassNamesWithValue(
                            i,
                            s.fork?.volume_spacers,
                          )}
                        >
                          {s.fork?.volume_spacers?.curr}
                        </td>
                      );
                    })}
                  </tr>
                )}
                {bike.fork.show_negative_psi && (
                  <tr>
                    <th className={styles.settingLabel}>
                      <small>{i18n.negative_air_pressure(true)}</small>
                    </th>
                    {settings?.map((s, i) => {
                      return (
                        <td
                          key={`fork-negative-air-pressure-${i}`}
                          className={createClassNamesWithValue(
                            i,
                            s.fork?.negative_psi,
                          )}
                        >
                          {s.fork?.negative_psi?.curr}
                        </td>
                      );
                    })}
                  </tr>
                )}
              </tbody>
            )}
            {bike?.shock && (
              <tbody>
                <tr className={styles.group}>
                  <th className={styles.groupLabel}>{i18n.shock}</th>
                  <th colSpan={colSpan}></th>
                </tr>
                {bike.shock.show_sag && (
                  <tr>
                    <th className={styles.settingLabel}>
                      <small>{i18n.sag}</small>
                    </th>
                    {settings?.map((s, i) => {
                      return (
                        <td
                          key={`shock-sag-${i}`}
                          className={createClassNamesWithValue(i, s.shock?.sag)}
                        >
                          {s.shock?.sag?.curr !== undefined &&
                            formatPercent(s.shock?.sag?.curr)}
                        </td>
                      );
                    })}
                  </tr>
                )}
                {bike.shock.type === "air" && (
                  <tr>
                    <th className={styles.settingLabel}>
                      <small>{i18n.air_pressure}</small>
                    </th>
                    {settings?.map((s, i) => {
                      return (
                        <td
                          key={`shock-air-pressure-${i}`}
                          className={createClassNamesWithValue(i, s.shock?.psi)}
                        >
                          {s.shock?.psi?.curr}
                        </td>
                      );
                    })}
                  </tr>
                )}
                {bike.shock.type === "coil" && (
                  <tr>
                    <th className={styles.settingLabel}>
                      <small>{i18n.spring_weight}</small>
                    </th>
                    {settings?.map((s, i) => {
                      return (
                        <td
                          key={`shock-air-pressure-${i}`}
                          className={createClassNamesWithValue(i, s.shock?.lbs)}
                        >
                          {s.shock?.lbs?.curr}
                        </td>
                      );
                    })}
                  </tr>
                )}
                {bike.shock.show_lsc && (
                  <tr>
                    <th className={styles.settingLabel}>
                      <small>{i18n.lsc(true)}</small>
                    </th>
                    {settings?.map((s, i) => {
                      return (
                        <td
                          key={`shock-spring-weight-${i}`}
                          className={createClassNamesWithValue(i, s.shock?.lsc)}
                        >
                          {s.shock?.lsc?.curr}
                        </td>
                      );
                    })}
                  </tr>
                )}
                {bike.shock.show_hsc && (
                  <tr>
                    <th className={styles.settingLabel}>
                      <small>{i18n.hsc(true)}</small>
                    </th>
                    {settings?.map((s, i) => {
                      return (
                        <td
                          key={`shock-hsc-${i}`}
                          className={createClassNamesWithValue(i, s.shock?.hsc)}
                        >
                          {s.shock?.hsc?.curr}
                        </td>
                      );
                    })}
                  </tr>
                )}
                {bike.shock.show_lsr && (
                  <tr>
                    <th className={styles.settingLabel}>
                      <small>{i18n.lsr(true, true)}</small>
                    </th>
                    {settings?.map((s, i) => {
                      return (
                        <td
                          key={`shock-lsr-${i}`}
                          className={createClassNamesWithValue(i, s.shock?.lsr)}
                        >
                          {s.shock?.lsr?.curr}
                        </td>
                      );
                    })}
                  </tr>
                )}
                {bike.shock.show_hsr && (
                  <tr>
                    <th className={styles.settingLabel}>
                      <small>{i18n.hsr(true)}</small>
                    </th>
                    {settings?.map((s, i) => {
                      return (
                        <td
                          key={`shock-hsr-${i}`}
                          className={createClassNamesWithValue(i, s.shock?.hsr)}
                        >
                          {s.shock?.hsr?.curr}
                        </td>
                      );
                    })}
                  </tr>
                )}
                {bike.shock.show_volume_spacers && (
                  <tr>
                    <th className={styles.settingLabel}>
                      <small>{i18n.volume_spacers}</small>
                    </th>
                    {settings?.map((s, i) => {
                      return (
                        <td
                          key={`shock-volume-spacers-${i}`}
                          className={createClassNamesWithValue(
                            i,
                            s.shock?.volume_spacers,
                          )}
                        >
                          {s.shock?.volume_spacers?.curr}
                        </td>
                      );
                    })}
                  </tr>
                )}
              </tbody>
            )}
            <tbody>
              <tr className={styles.group}>
                <th className={styles.groupLabel}>{i18n.wheels}</th>
                <th colSpan={colSpan}></th>
              </tr>
              {preferences?.tire_pressure_unit === "psi" && (
                <>
                  <tr>
                    <th className={styles.settingLabel}>
                      <small>{i18n.front}</small>
                    </th>
                    {settings?.map((s, i) => {
                      return (
                        <td
                          key={`wheels-front-psi-${i}`}
                          className={createClassNamesWithValue(
                            i,
                            s.wheels?.front_psi,
                          )}
                        >
                          {s.wheels?.front_psi?.curr}
                        </td>
                      );
                    })}
                  </tr>
                  <tr>
                    <th className={styles.settingLabel}>
                      <small>{i18n.rear}</small>
                    </th>
                    {settings?.map((s, i) => {
                      return (
                        <td
                          key={`wheels-rear-psi-${i}`}
                          className={createClassNamesWithValue(
                            i,
                            s.wheels?.rear_psi,
                          )}
                        >
                          {s.wheels?.rear_psi?.curr}
                        </td>
                      );
                    })}
                  </tr>
                </>
              )}
              {preferences?.tire_pressure_unit === "bar" && (
                <>
                  <tr>
                    <th className={styles.settingLabel}>
                      <small>{i18n.front}</small>
                    </th>
                    {settings?.map((s, i) => {
                      return (
                        <td
                          key={`wheels-front-bar-${i}`}
                          className={createClassNamesWithValue(
                            i,
                            s.wheels?.front_bar,
                          )}
                        >
                          {s.wheels?.front_bar?.curr}
                        </td>
                      );
                    })}
                  </tr>
                  <tr>
                    <th className={styles.settingLabel}>
                      <small>{i18n.rear}</small>
                    </th>
                    {settings?.map((s, i) => {
                      return (
                        <td
                          key={`wheels-rear-bar-${i}`}
                          className={createClassNamesWithValue(
                            i,
                            s.wheels?.rear_bar,
                          )}
                        >
                          {s.wheels?.rear_bar?.curr}
                        </td>
                      );
                    })}
                  </tr>
                </>
              )}
            </tbody>
            <tbody>
              <tr className={styles.group}>
                <th className={styles.groupLabel}>&nbsp;</th>
                <th colSpan={colSpan}></th>
              </tr>
              <tr>
                <th className={styles.settingLabel}>
                  <small>{i18n.notes}</small>
                </th>
                {settings?.map((s, i) => {
                  return (
                    <td
                      key={`notes-${i}`}
                      className={classNames(
                        styles.settingValue,
                        createClassNames(i),
                      )}
                    >
                      {s.notes?.curr !== undefined &&
                        s.notes.curr !== s.notes?.prev && (
                          <Icon
                            className={styles.notesIcon}
                            glyph={Tooltip}
                            onClick={() => {
                              setOpenNotesDialogIndex(i);
                            }}
                          />
                        )}
                      <BottomSheet
                        isOpen={openNotesDialogIndex === i}
                        onClose={() => {
                          setOpenNotesDialogIndex(undefined);
                        }}
                      >
                        <p>{s.notes?.curr}</p>
                      </BottomSheet>
                    </td>
                  );
                })}
              </tr>
            </tbody>
          </table>
        </div>
      ) : (
        <IllustratedMessage message="You have no settings" glyph={NoData} />
      )}
    </>
  );
};

export default HistoryTable;
